import { z } from "zod"

import { transactionQuestionClosedReasons } from "../../transaction-questions.js"

/* Questions about transactions. The shapes the request payload embeds live
   here, apart from the ask and accept responses, because portal.ts imports
   this file and a response that carried the request back would make a cycle. */

/** One row under a question, as the client saw it when it was asked. */
export const TransactionQuestionRowSchema = z.object({
  recordType: z.string(),
  recordIndex: z.number().int(),
  recordDate: z.string().nullable(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  description: z.string().nullable(),
})

export type TransactionQuestionRow = z.infer<
  typeof TransactionQuestionRowSchema
>

/** The question on a `transaction_question` item. `rows` are the live rows
    only; a row the firm categorized, a re-read changed or a posting took
    off counts in `changedRowCount` instead. */
export const TransactionQuestionPayloadSchema = z.object({
  documentId: z.string().uuid(),
  statementLabel: z.string(),
  counterpartyKey: z.string(),
  vendorLabel: z.string(),
  suggestedCategoryId: z.string().uuid().nullable(),
  rows: z.array(TransactionQuestionRowSchema),
  changedRowCount: z.number().int().nonnegative(),
  /** The statement posted while the question was open. */
  posted: z.boolean(),
  closedReason: z.enum(transactionQuestionClosedReasons).nullable(),
  acceptedCategoryId: z.string().uuid().nullable(),
  /** The accept also made a rule for every row from this vendor. */
  ruleMade: z.boolean(),
})

export type TransactionQuestionPayload = z.infer<
  typeof TransactionQuestionPayloadSchema
>

/** A category the client may pick: the firm's defaults and this client's own. */
export const TransactionQuestionCategoryChoiceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

/** On a review-page row that sits under a live question. */
export const ApiRowQuestionSchema = z.object({
  requestId: z.string().uuid(),
  itemId: z.string().uuid(),
  state: z.enum(["waiting", "answered"]),
  proposedCategoryId: z.string().uuid().nullable(),
  /** The client's note, or the one-tap answer they pressed. */
  note: z.string().nullable().optional(),
})

export type ApiRowQuestion = z.infer<typeof ApiRowQuestionSchema>

export const ApiTransactionQuestionPreviewResponseSchema = z.object({
  /** Vendor-and-statement groups, plus one per row with no vendor. */
  questionCount: z.number().int().nonnegative(),
  /** How many the next "Ask the client" sends: at most 25. */
  nextAskCount: z.number().int().nonnegative(),
  rowCount: z.number().int().nonnegative(),
  /** Money at stake, as a decimal string of absolute amounts. */
  total: z.string(),
  /** The open request the next ask adds to; null starts a new one. */
  target: z
    .object({ requestId: z.string().uuid(), title: z.string() })
    .nullable(),
})

export type ApiTransactionQuestionPreviewResponse = z.infer<
  typeof ApiTransactionQuestionPreviewResponseSchema
>

const AcceptRowKeySchema = z.object({
  recordType: z.string().min(1).max(40),
  recordIndex: z.number().int().nonnegative(),
})

export const ApiAcceptTransactionQuestionBodySchema = z.object({
  categoryId: z.string().uuid(),
  /** The answer the firm looked at; null when there was none. A different
      answer now means the client changed it, and the accept is refused. */
  expectedAnsweredAt: z.string().datetime().nullable(),
  /** Also make a rule for every row from this vendor for this client. */
  applyToVendor: z.boolean().default(false),
  /** The live rows the firm left ticked. Omitted means all of them. Ignored
      with `applyToVendor` on a vendor's question: the rule takes every row
      from the vendor, ticked or not. */
  rows: z.array(AcceptRowKeySchema).min(1).max(500).optional(),
})

export type ApiAcceptTransactionQuestionBody = z.input<
  typeof ApiAcceptTransactionQuestionBodySchema
>

export const ApiAcceptAllTransactionQuestionsBodySchema = z.object({
  applyToVendor: z.boolean().default(false),
})

export type ApiAcceptAllTransactionQuestionsBody = z.input<
  typeof ApiAcceptAllTransactionQuestionsBodySchema
>

export const ApiAskTransactionQuestionAgainBodySchema = z.object({
  note: z.string().trim().min(1).max(2000),
})

export type ApiAskTransactionQuestionAgainBody = z.input<
  typeof ApiAskTransactionQuestionAgainBodySchema
>
